import { z } from 'zod';

/** Monitoring (PRD §5.7). */
export const MONITOR_STATUSES = ['online', 'warning', 'offline', 'maintenance', 'unknown'] as const;
export type MonitorStatusCode = (typeof MONITOR_STATUSES)[number];

export const MONITOR_STATUS_LABELS: Record<MonitorStatusCode, string> = {
  online: 'Online',
  warning: 'Warning',
  offline: 'Offline',
  maintenance: 'Maintenance',
  unknown: 'Belum dicek',
};

/** Asset types monitored by default when they have an IP address (CPE only when switched on). */
export const MONITORED_BY_DEFAULT = ['OLT', 'SERVER', 'AP'] as const;

export interface PingResult {
  sent: number;
  received: number;
  lossPct: number;
  avgMs: number | null;
  error: string | null;
}

export interface Thresholds {
  failThreshold: number;
  latencyWarnMs: number;
  lossWarnPct: number;
}

/**
 * Next status from a ping (MON-02). A reply is `online`, or `warning` when slow or lossy.
 * No reply counts as a failure: `warning` until `failThreshold` failures in a row, then `offline`
 * (anti-flapping). A maintenance window overrides everything.
 */
export function evaluatePing(
  ping: PingResult,
  previous: { consecutiveFailures: number },
  t: Thresholds,
  inMaintenance: boolean,
): { status: MonitorStatusCode; consecutiveFailures: number; reason: string | null } {
  const failed = ping.received === 0;
  const consecutiveFailures = failed ? previous.consecutiveFailures + 1 : 0;
  if (inMaintenance) return { status: 'maintenance', consecutiveFailures, reason: 'Jadwal maintenance' };
  if (failed) {
    return consecutiveFailures >= t.failThreshold
      ? {
          status: 'offline',
          consecutiveFailures,
          reason: ping.error ?? `Tidak membalas ${consecutiveFailures}× berturut-turut`,
        }
      : {
          status: 'warning',
          consecutiveFailures,
          reason: `Tidak membalas (${consecutiveFailures}/${t.failThreshold})`,
        };
  }
  if (ping.lossPct >= t.lossWarnPct)
    return { status: 'warning', consecutiveFailures, reason: `Packet loss ${ping.lossPct}%` };
  if (ping.avgMs !== null && ping.avgMs > t.latencyWarnMs)
    return { status: 'warning', consecutiveFailures, reason: `Latensi ${Math.round(ping.avgMs)} ms` };
  return { status: 'online', consecutiveFailures, reason: null };
}

/** Summary lines of iputils `ping -q` → result. */
export function parsePingOutput(out: string, count: number): PingResult {
  const m = /(\d+) packets transmitted, (\d+) (?:packets )?received/.exec(out);
  if (!m) {
    const last = out.trim().split('\n').pop()?.trim();
    return { sent: count, received: 0, lossPct: 100, avgMs: null, error: last || 'Ping gagal dijalankan' };
  }
  const sent = Number(m[1]);
  const received = Number(m[2]);
  const rtt = /= [\d.]+\/([\d.]+)\//.exec(out);
  return {
    sent,
    received,
    lossPct: sent > 0 ? Math.round((1 - received / sent) * 100) : 100,
    avgMs: rtt ? Number(rtt[1]) : null,
    error: received === 0 ? 'Tidak ada balasan (timeout)' : null,
  };
}

const snmpBase = {
  enabled: z.boolean(),
  port: z.number().int().min(1).max(65535).default(161),
};

/**
 * SNMP settings. Secrets are write-only: leave them empty to keep what is stored.
 * v3: security level follows from which keys are given (noAuth / auth / authPriv).
 */
export const snmpSettingsSchema = z.discriminatedUnion('version', [
  z.object({ ...snmpBase, version: z.literal('1'), community: z.string().trim().max(100).optional() }),
  z.object({ ...snmpBase, version: z.literal('2c'), community: z.string().trim().max(100).optional() }),
  z.object({
    ...snmpBase,
    version: z.literal('3'),
    user: z.string().trim().min(1, 'User SNMPv3 wajib diisi').max(64),
    authProtocol: z.enum(['md5', 'sha']).default('sha'),
    authKey: z.string().max(128).optional(),
    privProtocol: z.enum(['des', 'aes']).default('aes'),
    privKey: z.string().max(128).optional(),
  }),
]);
export type SnmpSettings = z.infer<typeof snmpSettingsSchema>;

/** Stored (decrypted) SNMP credentials. */
export type SnmpCredentials =
  | { version: '1' | '2c'; community: string }
  | {
      version: '3';
      user: string;
      authProtocol: 'md5' | 'sha';
      authKey?: string;
      privProtocol: 'des' | 'aes';
      privKey?: string;
    };

export const monitoringConfigSchema = z.object({
  enabled: z.boolean(),
  intervalSec: z.number().int().min(30, 'Interval minimal 30 detik').max(86400),
  failThreshold: z.number().int().min(1).max(10),
  latencyWarnMs: z.number().int().min(1).max(60000),
  lossWarnPct: z.number().int().min(1).max(100),
  maintenanceUntil: z.string().datetime({ offset: true }).nullable(),
  snmp: snmpSettingsSchema.nullable(),
});
export type MonitoringConfigInput = z.infer<typeof monitoringConfigSchema>;

export const monitoringDevicesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  q: z.string().trim().max(100).optional(),
  status: z.enum(MONITOR_STATUSES).optional(),
  type: z.string().max(10).optional(),
  area: z.string().max(40).optional(),
});

export interface MonitoredDevice {
  assetId: string;
  code: string;
  name: string;
  typeCode: string;
  areaCode: string | null;
  ip: string;
  httpPort: number | null;
  httpsPort: number | null;
  enabled: boolean;
  status: MonitorStatusCode;
  since: string | null;
  lastCheckedAt: string | null;
  latencyMs: number | null;
  lossPct: number | null;
  lastError: string | null;
  cpuPct: number | null;
  memPct: number | null;
  uptimeS: number | null;
  sysName: string | null;
}

export interface MonitoringEvent {
  id: number;
  assetId: string;
  code: string;
  name: string;
  typeCode: string;
  areaCode: string | null;
  from: MonitorStatusCode;
  to: MonitorStatusCode;
  message: string | null;
  at: string;
}

export interface MonitoringSummary {
  counts: Record<MonitorStatusCode, number>;
  total: number;
  events: MonitoringEvent[];
}

export interface AssetMonitoring {
  monitored: boolean;
  ip: string | null;
  config: {
    enabled: boolean;
    intervalSec: number;
    failThreshold: number;
    latencyWarnMs: number;
    lossWarnPct: number;
    maintenanceUntil: string | null;
    snmp: null | {
      enabled: boolean;
      version: '1' | '2c' | '3';
      port: number;
      hasCredentials: boolean;
      user?: string;
      authProtocol?: string;
      privProtocol?: string;
    };
  };
  device: MonitoredDevice | null;
  /** Last 24 h of ping checks, oldest first. */
  checks: Array<{ at: string; status: MonitorStatusCode; latencyMs: number | null; lossPct: number | null }>;
  /** Share of the last 24 h checks that got a reply. */
  uptimePct24h: number | null;
  snmp: null | {
    collectedAt: string;
    sysName: string | null;
    sysDescr: string | null;
    sysLocation: string | null;
    sysContact: string | null;
    uptimeS: number | null;
    cpuPct: number | null;
    memPct: number | null;
    memTotalMb: number | null;
  };
  events: MonitoringEvent[];
}

/** Realtime payload on the `asset.status` Socket.IO event. */
export interface AssetStatusEvent {
  assetId: string;
  areaCode: string | null;
  status: MonitorStatusCode;
  previous: MonitorStatusCode;
  latencyMs: number | null;
  lossPct: number | null;
  checkedAt: string;
  changed: boolean;
}

export const MONITORING_EVENTS_CHANNEL = 'monitoring:events';
export const MONITORING_QUEUE = 'monitoring';
